/**
 * Assets tab of a pe3d variant, in the snapshot editor's Assets tab chrome
 * (snapshotEditor/containerAssets.js): a search box, one collapsible card per
 * kind of file, one card per slot (pe3dSlotCard.js).
 *
 * The slots are the game's schema — a variant cannot add or remove one — so
 * there is no Add or Delete here: each slot is the game's file until it is
 * replaced, and the card's reset puts that back.
 *
 * Buckets are per slot, like a versionedPlayEngine component's: the variant
 * keeps `dashboardMeta.buckets` ({ slot: bucket }). A slot with none shows the
 * bucket of the library file it uses, else "no bucket". The library is read
 * once per kind with every bucket in it, so switching a bucket costs nothing.
 */

import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Card, CardContent, Chip, Typography } from "@material-ui/core";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import InnerComponentIcon from "../../../images/gameConfig/Inner-component-icon.svg";
import PlayEngineTabSearch, { matchesTabSearch } from "../playEngineTabSearch";
import { FIELD_LABEL, PANEL_BG, SECTION_DIVIDER } from "../snapshotEditor/panelPalette";
import { LIBRARY, bucketsOf, listLibrary, slotBucket, slotChoices } from "../../modules/pe3dLibrary";
import { NO_BUCKET, normalizeBucket } from "../../modules/playEngineAssetUtils";
import Pe3dSlotCard from "./pe3dSlotCard";
import { CARD_STYLE, MUTED, PanelMessage } from "./pe3dPanelKit";
import { OverrideMark } from "./pe3dLanguageMark";

const KIND_LABELS = { model: "3D models", texture: "Textures", image: "Images", audio: "Sounds", video: "Videos", font: "Fonts", data: "Data", atlas: "Atlases", bitmapFont: "Bitmap fonts", spine: "Spine" };
const kindLabel = (kind) => KIND_LABELS[kind] || kind;
const groupItemId = (kind) => `pe3d-assets-${kind}`;

function EndIcon(props) {
  return <DisabledByDefaultRoundedIcon {...props} sx={{ opacity: 0.3 }} />;
}

export default function ContainerPe3dAssets({ editor, canEdit, createSnackbar }) {
  const { bundle } = editor.release;
  const { doc } = editor;
  const slots = Object.entries(bundle.schema.assets || {});
  const storedBuckets = _.get(doc, "dashboardMeta.buckets") || {};
  const [search, setSearch] = useState("");
  // Library files per slot kind, every bucket; null while a kind loads.
  const [libraries, setLibraries] = useState({});
  const kinds = _.uniq(slots.map(([, def]) => def.kind));
  const [expandedItems, setExpandedItems] = useState(kinds.map(groupItemId));

  const loadKind = (kind) =>
    listLibrary(kind)
      .then((items) => setLibraries((current) => ({ ...current, [kind]: items })))
      .catch((err) => {
        setLibraries((current) => ({ ...current, [kind]: [] }));
        createSnackbar(`Could not list the media library: ${err.message}`, "error", false);
      });
  useEffect(() => {
    const libraryKinds = kinds.filter((kind) => LIBRARY[kind]);
    setLibraries(Object.fromEntries(libraryKinds.map((kind) => [kind, null])));
    libraryKinds.forEach(loadKind);
    setExpandedItems(kinds.map(groupItemId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kinds.join(",")]);

  const { buckets, countsByKind } = useMemo(() => bucketsOf(libraries, storedBuckets), [libraries, JSON.stringify(storedBuckets)]);

  if (!slots.length) {
    return (
      <PanelMessage>This game has no replaceable files.</PanelMessage>
    );
  }

  // The language being edited: its own file, or en's when it sets none (modules/pe3dLocalization.js).
  const setSlot = (slot, ref) =>
    editor.editView((variant) => {
      const assets = { ...(variant.assets || {}) };
      if (ref) assets[slot] = ref;
      else delete assets[slot];
      if (_.isEmpty(assets)) delete variant.assets;
      else variant.assets = assets;
      return variant;
    });
  const setBucket = (slot, value) => {
    const next = { ...storedBuckets, [slot]: normalizeBucket(value) };
    if (next[slot] === NO_BUCKET) delete next[slot];
    editor.updateMeta(doc._id, { buckets: next });
  };
  const notifyReadOnly = () => createSnackbar("You do not have permission to edit this variant.", "warning", false);

  const matches = ([slot, def]) => matchesTabSearch(`${slot} ${def.label || ""}`, search);
  const visibleKinds = kinds.filter((kind) => slots.some((entry) => entry[1].kind === kind && matches(entry)));
  const expanded = search.trim() ? _.uniq([...expandedItems, ...visibleKinds.map(groupItemId)]) : expandedItems;

  return (
    <div style={{ padding: "8px 4px", backgroundColor: PANEL_BG, minHeight: "100%" }}>
      <PlayEngineTabSearch value={search} onChange={setSearch} />
      {search.trim() && visibleKinds.length === 0 && (
        <Typography variant="body2" style={{ color: MUTED, padding: 8 }}>
          No slots match this search.
        </Typography>
      )}
      <SimpleTreeView
        aria-label="pe3d-assets"
        expandedItems={expanded}
        onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
        slots={{ expandIcon: ArrowRightIcon, collapseIcon: ArrowDropDownIcon, endIcon: EndIcon }}
        sx={{ overflowX: "hidden", flexGrow: 1 }}
      >
        {visibleKinds.map((kind) => {
          const kindSlots = slots.filter((entry) => entry[1].kind === kind && matches(entry));
          return (
            <Card key={kind} style={{ margin: "5px", ...CARD_STYLE }}>
              <TreeItem
                itemId={groupItemId(kind)}
                label={
                  <div style={{ display: "flex", alignItems: "center", minHeight: "32px", fontWeight: "bold" }}>
                    <img src={InnerComponentIcon} alt="" style={{ width: "18px", height: "18px", marginRight: "8px" }} />
                    <span style={{ color: "#eee" }}>{kindLabel(kind)}</span>
                    <Chip label={kindSlots.length} size="small" style={{ marginLeft: 8, backgroundColor: PANEL_BG, color: FIELD_LABEL, height: 20 }} />
                  </div>
                }
              >
                <Card style={{ backgroundColor: PANEL_BG, boxShadow: "none", borderTop: `1px solid ${SECTION_DIVIDER}`, borderRadius: 0 }}>
                  <CardContent style={{ paddingTop: 8, paddingBottom: 12 }}>
                    {kindSlots.map(([slot, def]) => {
                      const value = (editor.view.assets || {})[slot] || null;
                      const all = libraries[kind];
                      const bucket = slotBucket(slot, value, storedBuckets, all);
                      return (
                        <Pe3dSlotCard
                          key={slot}
                          slot={slot}
                          def={def}
                          value={value}
                          release={editor.release}
                          releaseChoices={slotChoices(bundle, slot)}
                          libraryItems={all === null ? null : (all || []).filter((item) => item.bucket === bucket)}
                          bucket={bucket}
                          buckets={buckets}
                          bucketCounts={countsByKind[kind]}
                          canEdit={canEdit}
                          createSnackbar={createSnackbar}
                          onChange={(ref) => setSlot(slot, ref)}
                          onBucketChange={(next) => normalizeBucket(next) !== bucket && setBucket(slot, next)}
                          onUploaded={() => loadKind(kind)}
                          onReadOnly={notifyReadOnly}
                          mark={<OverrideMark kind="assets" fieldKey={slot} disabled={!canEdit} />}
                        />
                      );
                    })}
                  </CardContent>
                </Card>
              </TreeItem>
            </Card>
          );
        })}
      </SimpleTreeView>
      <Typography variant="caption" style={{ color: MUTED, display: "block", padding: "8px 6px" }}>
        A slot uses the game&apos;s own file until the variant replaces it. Every file is checked against the slot&apos;s limits before it can
        be saved.
      </Typography>
    </div>
  );
}
